import React from 'react'
import { t } from 'ttag'

import { Details } from './Details/Details'
import { Overview } from './Overview/Overview'
import { Invites } from './Invites/Invites'
import { Tests } from './Tests/Tests'
import { Interviews } from './Interviews/Interviews'
import { Results } from './Results/Results'

import { TAB_NAMES } from './../model'

import {
  DownloadCloudIcon,
  Edit3Icon,
  MoveIcon,
  Trash2Icon,
  UserCheckIcon,
  UserXIcon,
} from 'components/shared/Icon/model'

const DEFAULT = 0
const HIRED = 1
const REJECTED = 2

export const HIRE_STATUSES = { DEFAULT, HIRED, REJECTED }

export const STATUSES_WITH_HIRE = ['completed', 'screened', 'interviewed']

const DELETE = 'DELETE'
const EDIT = 'EDIT'
const EXPORT = 'EXPORT'
const MOVE = 'MOVE'
const HIRE = 'HIRE'

export const ACTIONS = { MOVE, EXPORT, DELETE }

export const ROW_ACTIONS = { EDIT, MOVE, DELETE, HIRE }

export const getActions = ({ count, hasMoveCandidatesFeature }) => {
  const entity = count !== 1 ? t`candidates` : t`candidate`

  return [
    ...(hasMoveCandidatesFeature
      ? [
          {
            icon: MoveIcon,
            label: t`Move`,
            note: t`Move candidates to selection step or to a different project`,
            type: ACTIONS.MOVE,
          },
        ]
      : []),
    {
      icon: DownloadCloudIcon,
      label: t`Export`,
      note: count
        ? t`Export ${count} selected ${entity} to spreadsheet`
        : t`No ${entity} selected for export`,
      type: ACTIONS.EXPORT,
    },
    {
      icon: Trash2Icon,
      label: t`Remove`,
      note: count
        ? t`Delete ${count} selected ${entity}`
        : t`No ${entity} selected for deletion`,
      type: ACTIONS.DELETE,
    },
  ]
}

export const getRowActions = ({
  hasMoveCandidatesFeature,
  showHireAction,
  isHired,
}: {
  hasMoveCandidatesFeature?: boolean
  showHireAction?: boolean
  isHired?: boolean
} = {}) => [
  { icon: Edit3Icon, label: t`Edit`, type: ROW_ACTIONS.EDIT },
  ...(showHireAction
    ? [
        {
          icon: isHired ? UserXIcon : UserCheckIcon,
          label: isHired ? t`Unmark as hired` : t`Mark as hired`,
          type: ROW_ACTIONS.HIRE,
        },
      ]
    : []),
  ...(hasMoveCandidatesFeature
    ? [{ icon: MoveIcon, label: t`Move`, type: ROW_ACTIONS.MOVE }]
    : []),
  { icon: Trash2Icon, label: t`Remove`, type: ROW_ACTIONS.DELETE },
]

export const getTabs = state => {
  const { hasInterviewModule } = state

  const JOB_DETAILS_TABS = [
    { id: TAB_NAMES.DETAILS, label: t`Details` },
    { id: TAB_NAMES.OVERVIEW, label: t`Candidate overview` },
    { id: TAB_NAMES.INVITES, label: t`Send invites` },
    { id: TAB_NAMES.TESTS, label: t`Test scores` },
    ...(hasInterviewModule
      ? [
          { id: TAB_NAMES.INTERVIEWS, label: t`Interviews` },
          { id: TAB_NAMES.RESULTS, label: t`Final results` },
        ]
      : []),
  ]

  return JOB_DETAILS_TABS.map((tab, index) => ({
    sequenceNumber: index + 1,
    ...tab,
  }))
}

export const getTab = state => {
  const { activeTab, project, hasInterviewModule, isInterviewAvailable } = state

  const tabs = {
    [TAB_NAMES.DETAILS]: <Details {...{ project }} />,
    [TAB_NAMES.OVERVIEW]: (
      <Overview {...{ activeTab, project, hasInterviewModule }} />
    ),
    [TAB_NAMES.INVITES]: (
      <Invites {...{ activeTab, project, hasInterviewModule }} />
    ),
    [TAB_NAMES.TESTS]: (
      <Tests {...{ activeTab, project, hasInterviewModule }} />
    ),
    ...(hasInterviewModule && {
      [TAB_NAMES.INTERVIEWS]: (
        <Interviews
          {...{ activeTab, project, hasInterviewModule, isInterviewAvailable }}
        />
      ),
      [TAB_NAMES.RESULTS]: (
        <Results
          {...{ activeTab, project, hasInterviewModule, isInterviewAvailable }}
        />
      ),
    }),
  }

  return tabs[activeTab]
}

export const getFiltersConfigs = (keys?: string[]) =>
  [
    {
      key: 'is_favourite',
      label: t`Show favorites only`,
      defaultValue: false,
      toggleValue: bool => !bool,
      getQuery: filter => (filter.is_favourite ? { is_favourite: true } : {}),
    },
    {
      key: 'hire_status',
      label: t`Show hired only`,
      defaultValue: 0,
      toggleValue: num => Number(!num),
      getQuery: filter => (filter.hire_status ? { hire_status: 1 } : {}),
    },
  ].filter(conf => !keys || keys.includes(conf.key))
